<template>
  <div class="textarea-input-box" :class="classes">
    <div class="textarea-input-box__wrapper">
      <textarea
        ref="input"
        :aria-label="label"
        v-bind="{ id, ...$attrs, placeholder, value: inputValue, name, type, tabindex }"
        v-on="listeners"
        class="textarea-input-box__input"
        :class="{ 'textarea-input-box__input--icon': hasSlotSendIcon }"
        :style="minHeightVariable"
      ></textarea>

      <div v-if="hasSlotSendIcon && !!inputValue" class="textarea-input-box__send">
        <slot name="sendIcon" />
      </div>
    </div>

    <div class="error-container" v-if="error && errorMessage">
      <div class="error">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'TextareaInputBox',

  inheritAttrs: false,

  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },

    type: {
      type: String,
      required: false,
      default: 'text',
    },

    modelValue: {
      required: true,
      type: String,
    },

    placeholder: {
      type: String,
      default: '',
      required: false,
    },

    label: {
      type: String,
      required: true,
    },

    error: {
      type: Boolean,
      default: false,
      required: false,
    },

    errorMessage: {
      type: String,
      required: false,
      default: '',
    },

    tabindex: {
      type: Number,
      default: 0,
      required: false,
    },

    name: {
      type: String,
      required: false,
      default: null,
    },

    focusOnMount: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether to scroll to the textarea when it gets focused
     */
    scrollOnFocus: {
      type: Boolean,
      default: true,
    },

    color: {
      type: String,
      default: 'grey',
      validator(value: string) {
        return ['grey'].indexOf(value) !== -1;
      },
    },

    minHeight: {
      type: Number,
      default: 40,
    },
  },
  emits: { 'update:modelValue': null, focus: null, blur: null },

  data(): { inputValue: string; focused: boolean } {
    return {
      inputValue: '',
      focused: false,
    };
  },

  computed: {
    // Had to use a function here to avoid a TS error
    // because --min-height is not a valid CSS property to use in the "style" attribute.
    // Using a string also failed.
    minHeightVariable(): {} {
      return { '--min-height': `${this.minHeight}px` };
    },

    classes(): Array<string | Record<string, boolean>> {
      return [`textarea-input-box--color-${this.color}`, { 'textarea-input-box--error': this.error }];
    },

    listeners(): Record<string, (event: any) => void> {
      // eslint-disable-next-line consistent-this
      const vm = this;

      return {
        input(event: InputEvent) {
          const target = event.target as HTMLTextAreaElement;
          const inputValue = target.value;

          vm.inputValue = inputValue;
          vm.$emit('update:modelValue', inputValue);
          vm.resize(target);
        },

        focus() {
          vm.focused = true;
          vm.$emit('focus');
        },

        blur(event: FocusEvent) {
          vm.focused = false;

          vm.$emit('blur', (event.target as HTMLTextAreaElement).value);
        },
      };
    },

    hasSlotSendIcon() {
      return hasSlotContent(this.$slots.sendIcon, {});
    },
  },

  methods: {
    focus() {
      (this.$refs.input as HTMLInputElement).focus({ preventScroll: !this.scrollOnFocus });
    },

    /**
     *  @public
     */
    blur() {
      (this.$refs.input as HTMLInputElement).blur();
    },

    /**
     *  @public
     */
    selectInput() {
      (this.$refs.input as HTMLInputElement).select();
    },

    /**
     *  @public
     */
    clear() {
      this.inputValue = '';
      this.$emit('update:modelValue', this.inputValue);
    },

    resize(textarea: HTMLTextAreaElement) {
      // first reset the textarea height
      textarea.style.height = 'var(--min-height)';

      const scrollHeight = textarea.scrollHeight;
      // set the height to fit the content
      textarea.style.height = `${scrollHeight}px`;
    },
  },

  mounted() {
    this.inputValue = this.modelValue;

    if (!this.focusOnMount) return;
    this.focus();
  },

  watch: {
    modelValue(val) {
      if (this.error) {
        return;
      }

      this.inputValue = val;
    },

    inputValue(val) {
      // Check for error state
      if (this.error) {
        return;
      }

      // We don't need to check if value is empty since the resize function handles that
      if (!val) {
        this.$nextTick(() => {
          this.resize(this.$refs.input);
        });
      }
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';

.textarea-input-box {
  position: relative;
  width: 100%;
  color: colors.$color-grey-800;

  @include typography.typography-14-normal();
}

.textarea-input-box__wrapper {
  position: relative;
  display: flex;
  border-radius: settings.$border-radius;
}

.textarea-input-box__send {
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.textarea-input-box__input {
  border-radius: settings.$border-radius;
  border: none;
  outline: none;
  height: var(--min-height); // defined on the component's HTML
  max-height: 106px;
  width: 100%;
  padding: 10px 12px 10px 12px;
  transition: all 0.1s ease-in-out;
  // deactivate transitions only on the 'height' property to avoid problems with auto-resizing
  transition: height 0s;
  resize: none;

  &--icon {
    padding-right: 38px;
  }

  // Don't show error outline in FF
  &:invalid {
    box-shadow: none;
  }
}

/**
 * Colors
 */

/**
 * color grey (default)
 */
.textarea-input-box--color-grey .textarea-input-box__wrapper {
  .textarea-input-box__input {
    color: colors.$color-grey-800;
    background: colors.$color-grey-100;
    box-shadow: settings.$box-shadow-border-grey-50;

    &::placeholder {
      color: colors.$color-placeholder;
    }
  }

  // typing, filled
  .textarea-input-box__input:not(:placeholder-shown):not(:focus-within) {
    color: colors.$color-grey-600;
  }

  &:hover {
    .textarea-input-box__input:not(:disabled) {
      background: colors.$color-white;
    }
  }

  &:focus-within {
    .textarea-input-box__input:not(:disabled) {
      background: colors.$color-white;
      border: none;
      box-shadow: settings.$box-shadow-focus-ring, 0 0 0 1px colors.$color-blue-300;
      caret-color: colors.$color-blue-600;
    }
  }

  .textarea-input-box__input:disabled {
    color: colors.$color-grey-300;
    background: colors.$color-grey-200;
    cursor: not-allowed;

    &::placeholder {
      color: colors.$color-grey-300;
    }
  }
}

.textarea-input-box--error {
  .textarea-input-box__wrapper {
    border: 1px solid colors.$input-error;
  }
}

.textarea-input-box .error-container {
  margin: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding: 8px 16px;
}
</style>
